import { Component, Input, Output, EventEmitter, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DynamicFormsV2Component } from '../dynamic-forms-v2/dynamic-forms-v2.component';
import { MatDividerModule } from '@angular/material/divider';
import { SplitHashPipe } from "../split-hash.pipe";
import { ServiceRequestSelectorDialogComponent } from '../../lab-requests/service-request-selector-dialog/service-request-selector-dialog.component';
import { MedicineSearchDialogComponent, MedicineSearchResult } from '../../medicine-requests/medicine-search-dialog/medicine-search-dialog.component';

/**
 * Generic resource data review component.
 * Reusable for any FHIR resource (or arbitrary object) given form field definitions and submitted-like data.
 * It mirrors the editing workflow of PatientDataReviewComponent but pushes mapping responsibilities outward via callbacks.
 */
@Component({
    selector: 'app-resource-data-review',
    standalone: true,
    imports: [CommonModule, MatCardModule,
        MatDividerModule,
        MatButtonModule, MatIconModule, MatDialogModule, SplitHashPipe],
    templateUrl: './resource-data-review.component.html',
    styleUrls: ['./resource-data-review.component.scss']
})
export class ResourceDataReviewComponent implements OnInit {
    @Input() resource: any; // Original resource object (FHIR Organization, Patient, etc.)
    @Input() formFields: any[] = []; // Dynamic form field definitions
    @Input() submittedData?: any; // Optional pre-mapped submitted data
    @Input() mapResourceToSubmittedData?: (resource: any, formFields: any[]) => any; // Mapping inbound
    @Input() applySubmittedDataToResource?: (submitted: any, resource: any) => any; // Mapping outbound
    @Input() showEditButtons: boolean = true;

    @Output() fieldEdited = new EventEmitter<{ fieldApiName: string; newValue: any; isArray?: boolean; arrayIndex?: number }>();
    @Output() resourceUpdated = new EventEmitter<any>();

    dialog = inject(MatDialog);

    simpleFields: any[] = [];
    arrayFields: any[] = [];
    groupedArrayFields: { [key: string]: any[] } = {};

    ngOnInit() {
// $#$        // /* alert(JSON.stringify(this.submittedData) */)
        if (!this.submittedData) {
            if (this.mapResourceToSubmittedData) {
                this.submittedData = this.mapResourceToSubmittedData(this.resource, this.formFields) || {};
            } else {
                // Fallback shallow mapping: copy primitive & object references by fieldApiName
                this.submittedData = {};
// $#$                // /* alert(JSON.stringify(this.resource) */)
                this.formFields.forEach(f => {
                    const k = f.generalProperties.fieldApiName;
                    (this.submittedData as any)[k] = (this.resource as any)[k];
                });
            }
        }
        this.categorizeFields();
    }
    specialCaseFields: any[] = [];
    private categorizeFields() {
        this.simpleFields = [];
        this.arrayFields = [];
        this.specialCaseFields = [];
        this.groupedArrayFields = {};
        this.formFields.forEach((field: any) => {
            const apiName = field.generalProperties.fieldApiName;
            if (field.generalProperties.isHidden &&
                field.generalProperties.fieldApiName !== 'medicationRequestMedicationGroupField'
            ) return;
            if (field.generalProperties.isArray) {
                const dataArray = this.submittedData[apiName];
                if (!this.groupedArrayFields[apiName]) this.groupedArrayFields[apiName] = [];
                if (Array.isArray(dataArray)) {
                    dataArray.forEach((item: any, index: number) => {
                        const arrayItem = {
                            field,
                            fieldApiName: apiName,
                            fieldLabel: field.generalProperties.fieldLabel || field.generalProperties.fieldName,
                            data: item,
                            index,
                            totalCount: dataArray.length
                        };
                        this.arrayFields.push(arrayItem);
                        this.groupedArrayFields[apiName].push(arrayItem);
                    });
                }
            } else {
                this.simpleFields.push({
                    field,
                    fieldApiName: apiName,
                    fieldLabel: field.generalProperties.fieldLabel || field.generalProperties.fieldName,
                    data: this.submittedData[apiName]
                });
            }
            if (field.generalProperties.fieldApiName == 'medicationRequestMedicationGroupField') {
                this.specialCaseFields.push({
                    field,
                    fieldApiName: apiName,
                    fieldLabel: field.generalProperties.fieldLabel || field.generalProperties.fieldName,
                    data: this.submittedData[apiName]
                })
            }
            //serviceRequestCategory and serviceRequestCode
            if (field.generalProperties.fieldApiName == 'ServiceRequestCode') {
                this.specialCaseFields.push({
                    field,
                    fieldApiName: apiName,
                    fieldLabel: field.generalProperties.fieldLabel || field.generalProperties.fieldName,
                    data: this.submittedData[apiName]
                })

            }
            if (field.generalProperties.fieldApiName == 'ServiceRequestCategory'
                && !field.generalProperties.isHidden
            ) {
                this.specialCaseFields.push({
                    field,
                    fieldApiName: apiName,
                    fieldLabel: field.generalProperties.fieldLabel || field.generalProperties.fieldName,
                    data: this.submittedData[apiName]
                })
            }
        });
    }
    editSpecialCaseField(fieldApiName: string) {
        if (!fieldApiName) return;

        // Only handle the known special-case for now
        if (fieldApiName == 'medicationRequestMedicationGroupField') {

            // Stock meds are already embedded in the option label by AddMedicineRequestsComponent,
            // and we don't have backend stock list here. Fall back to "empty stock list".
            // (RxNorm search still works.)
            const dialogRef = this.dialog.open(MedicineSearchDialogComponent, {
                width: '720px',
                maxHeight: '90vh',
                autoFocus: false,
                data: { stock: this.specialCaseFields.find(f => f.fieldApiName === fieldApiName)?.field.groupFields?.medicationRequestMedicine?.data || [] }
            });

            dialogRef.afterClosed().subscribe((opt: MedicineSearchResult | undefined) => {
                if (!opt) return;

                // Keep the same shape you already use in AddMedicineRequestsComponent:
                // values.medicationRequestMedicationGroupField.medicationRequestMedicine = <MedicineSearchResult>
                const nextValue = {
                    ...(this.submittedData?.[fieldApiName] ?? {}),
                    medicationRequestMedicine: opt
                };

                this.submittedData = this.submittedData ?? {};
                this.submittedData[fieldApiName] = nextValue;

                this.fieldEdited.emit({ fieldApiName, newValue: nextValue });
                this.applyAndEmit();
                this.categorizeFields();
            });
        } else if (fieldApiName == 'ServiceRequestCode') {

            let isCategoryHidden = false;
            //get category field from Formields
            isCategoryHidden = this.formFields.find((field: any) => {

                if (field.generalProperties.fieldApiName == 'ServiceRequestCategory') {
// $#$                    // /* alert('isCategoryHidden: ' + JSON.stringify(field.generalProperties) */);
                    return true
                } else {
                    return false;
                }
            })?.generalProperties?.isHidden || false;
// $#$            // /* alert(JSON.stringify(this.submittedData?.['ServiceRequestCategory']) */)
            this.dialog.open(ServiceRequestSelectorDialogComponent, {
                data: {
                    field: isCategoryHidden ? 'code' : 'both',

                    ...(this.submittedData?.['ServiceRequestCategory'] ? {
                        selectedCategory: this.submittedData?.['ServiceRequestCategory']
                    } : {}),
                    initialValue: {

                        code: this.submittedData?.['ServiceRequestCode']
                    }

                }
            }).afterClosed().subscribe(result => {
                // console.log(result.code);
                if (!result || !result.code) return;

                // Keep the same shape you already use in AddMedicineRequestsComponent:
                // values.medicationRequestMedicationGroupField.medicationRequestMedicine = <MedicineSearchResult>
                const nextValue = {
                    ...(typeof result === 'object' ? { ...result.code } : { text: result.code })
                };

                this.submittedData = this.submittedData ?? {};
                this.submittedData['ServiceRequestCode'] = nextValue;

                if (result.category) {
                    this.submittedData['ServiceRequestCategory'] = result.category;
                    this.fieldEdited.emit({ fieldApiName: 'ServiceRequestCategory', newValue: result.category });
                }

                this.fieldEdited.emit({ fieldApiName, newValue: nextValue });
                this.applyAndEmit();
                this.categorizeFields();
                // { code, display, system, active }
            });

        }
    }
    // Editing workflows
    editSimpleFields() {
        const fieldsWithValues = this.simpleFields.map(sf => {
            const copy = JSON.parse(JSON.stringify(sf.field));
            this.setFieldValue(copy, sf.data);
            copy.generalProperties.isArray = false;
            if (copy.generalProperties.isGroup && copy.groupFields) {
                Object.keys(copy.groupFields).forEach(k => copy.groupFields[k].generalProperties.isArray = false);
            }
            return copy;
        });
        const dialogRef = this.dialog.open(DynamicFormsV2Component, {
            width: '800px',
            maxHeight: '90vh',
            data: {
                formMetaData: { formName: 'Edit Details', submitText: 'Save Changes', closeDialogOnSubmit: true },
                formFields: fieldsWithValues
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result?.values) {
                Object.keys(result.values).forEach(k => {
                    this.submittedData[k] = result.values[k];
                    this.fieldEdited.emit({ fieldApiName: k, newValue: result.values[k], isArray: false });
                });
                this.applyAndEmit();
                this.categorizeFields();
            }
        });
    }

    addArrayItem(fieldApiName: string) {
        const fieldDef = this.formFields.find(f => f.generalProperties.fieldApiName === fieldApiName);
        if (!fieldDef) return;
        const emptyItem = this.createEmptyArrayItem(fieldDef);
        const fieldCopy = JSON.parse(JSON.stringify(fieldDef));
        fieldCopy.generalProperties.isArray = false;
        if (fieldCopy.generalProperties.isGroup && fieldCopy.groupFields) {
            Object.keys(fieldCopy.groupFields).forEach(k => fieldCopy.groupFields[k].generalProperties.isArray = false);
        }
        this.setFieldValue(fieldCopy, emptyItem);
        const dialogRef = this.dialog.open(DynamicFormsV2Component, {
            width: '600px',
            maxHeight: '93vh',
            data: {
                formMetaData: { formName: `Add ${fieldCopy.generalProperties.fieldLabel || fieldCopy.generalProperties.fieldName}`, submitText: 'Add', closeDialogOnSubmit: true },
                formFields: [fieldCopy]
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result?.values) {
                const newValue = result.values[fieldApiName];
                if (!Array.isArray(this.submittedData[fieldApiName])) this.submittedData[fieldApiName] = [];
                this.submittedData[fieldApiName].push(newValue);
                this.fieldEdited.emit({ fieldApiName, newValue: this.submittedData[fieldApiName], isArray: true });
                this.applyAndEmit();
                this.categorizeFields();
            }
        });
    }

    editArrayItem(arrayFieldInfo: any) {
        const { field, fieldApiName, data, index } = arrayFieldInfo;
        const fieldCopy = JSON.parse(JSON.stringify(field));
        fieldCopy.generalProperties.isArray = false;
        if (fieldCopy.generalProperties.isGroup && fieldCopy.groupFields) {
            Object.keys(fieldCopy.groupFields).forEach(k => fieldCopy.groupFields[k].generalProperties.isArray = false);
        }
        this.setFieldValue(fieldCopy, data);
        const dialogRef = this.dialog.open(DynamicFormsV2Component, {
            width: '600px',
            maxHeight: '93vh',
            data: {
                formMetaData: { formName: `Edit ${field.generalProperties.fieldLabel || field.generalProperties.fieldName} #${index + 1}`, submitText: 'Save Changes', closeDialogOnSubmit: true },
                formFields: [fieldCopy]
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result?.values) {
                const updated = result.values[fieldApiName];
                this.submittedData[fieldApiName][index] = updated;
                this.fieldEdited.emit({ fieldApiName, newValue: updated, isArray: true, arrayIndex: index });
                this.applyAndEmit();
                this.categorizeFields();
            }
        });
    }

    deleteArrayItem(arrayFieldInfo: any) {
        const { fieldApiName, index } = arrayFieldInfo;
        if (!confirm('Delete this entry?')) return;
        this.submittedData[fieldApiName].splice(index, 1);
        this.fieldEdited.emit({ fieldApiName, newValue: this.submittedData[fieldApiName], isArray: true });
        this.applyAndEmit();
        this.categorizeFields();
    }

    private applyAndEmit() {
        if (this.applySubmittedDataToResource) {
            this.resource = this.applySubmittedDataToResource(this.submittedData, this.resource);
        } else {
            console.log(this.submittedData);
            console.log(this.resource);
            // Shallow merge fallback
            Object.keys(this.submittedData).forEach(k => (this.resource as any)[k] = this.submittedData[k]);
        }
        this.resourceUpdated.emit(this.resource);
    }

    private createEmptyArrayItem(fieldDef: any): any {
        if (fieldDef.generalProperties.isGroup && fieldDef.groupFields) {
            const obj: any = {};
            Object.keys(fieldDef.groupFields).forEach(k => {
                const sf = fieldDef.groupFields[k];
                obj[k] = sf.generalProperties.isArray ? [] : '';
            });
            return obj;
        }
        return '';
    }

    private setFieldValue(field: any, value: any) {
        if (field.generalProperties.isGroup && field.groupFields) {
            Object.keys(field.groupFields).forEach(k => {
                if (value && value.hasOwnProperty(k)) field.groupFields[k].generalProperties.value = value[k];
            });
        } else {
            field.generalProperties.value = value;
        }
    }

    // Template helpers
    getGroupedArrayKeys(): string[] { return Object.keys(this.groupedArrayFields); }
    getArrayItemsForField(apiName: string): any[] { return this.groupedArrayFields[apiName] || []; }
    objectKeys(o: any): string[] { return o ? Object.keys(o) : []; }
    isSubfieldHidden(arrayItem: any, key: string): boolean {
        const gf = arrayItem.field?.groupFields?.[key];
        return gf?.generalProperties?.isHidden === true;
    }
    getSubfieldLabel(arrayItem: any, key: string): string {
        const gf = arrayItem.field?.groupFields?.[key];
        return gf?.generalProperties?.fieldLabel || gf?.generalProperties?.fieldName || key;
    }

    getGroupFieldSubfieldLabel(field: any, key: string): string {
        const gf = field?.groupFields?.[key];
// $#$        // /* alert(JSON.stringify(gf) */);
        return gf?.generalProperties?.fieldLabel || gf?.generalProperties?.fieldName || key;
    }

    isGroupFieldSubfieldHidden(field: any, key: string): boolean {
        const gf = field?.groupFields?.[key];
        return gf?.generalProperties?.isHidden === true;
    }

    private readonly dtfDate = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    private readonly dtfDateTime = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    private tryFormatIsoDateString(value: string): string | null {
        const s = (value ?? '').toString().trim();
        if (!s) return null;

        // Matches common ISO-8601 date/datetime forms used by FHIR:
        // - YYYY-MM-DD
        // - YYYY-MM-DDTHH:mm[:ss[.sss]][Z|+HH:mm|-HH:mm]
        const isoLike = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)?(?:Z|[+-]\d{2}:\d{2})?$/;
        if (!isoLike.test(s)) return null;

        // Date-only strings are interpreted as UTC by `new Date('YYYY-MM-DD')` in many runtimes.
        // To avoid off-by-one in some timezones, construct as a local date.
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            const [yy, mm, dd] = s.split('-').map((x) => Number(x));
            if (!yy || !mm || !dd) return null;
            const d = new Date(yy, mm - 1, dd);
            return Number.isNaN(d.getTime()) ? null : this.dtfDate.format(d);
        }

        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return null;
        return this.dtfDateTime.format(d);
    }

    formatReviewValue(value: any, fallbackIndex: number = 1): string {
        if (value === undefined || value === null || value === '') {
            return '';
        }

        // Material Datepicker with `provideNativeDateAdapter()` yields real `Date` objects.
        // If we stringify them, we get verbose outputs like "Thu Feb 12 2026 01:30:00 GMT+0100 (...)".
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return '';
            const hasTime = value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0 || value.getMilliseconds() !== 0;
            return hasTime ? this.dtfDateTime.format(value) : this.dtfDate.format(value);
        }

        if (typeof value === 'string') {
            const formattedDate = this.tryFormatIsoDateString(value);
            if (formattedDate) return formattedDate;

            const parts = value.split('$#$');
            if (parts.length > fallbackIndex) {
                return parts[fallbackIndex];
            }
            return parts[0];
        }
        if (typeof value === 'object') {

            const display = value.display || value.label || value.text || value.name || value.value;
            if (typeof display === 'string') {
                return display;
            }
        }
        return String(value);
    }
}
